import { type JSX, useCallback } from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui/next';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageFcm } from 'src/app/pages/starterDashboardV2/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

import { PREVIEW_CLOSE_EVENT, SUGGESTED_TEMPLATE_NOTICE } from '../../../../constants';
import { useCreatePetitionUpdateContext } from '../../context';
import { PreviewEmailButton } from '../PreviewEmailButton/index_new';

import { PreviewEmailModalBody } from './components/PreviewEmailModalBody/index_new';

type PreviewModalProps = {
	disabled: boolean;
	onPreview: () => void;
	emailTitle: string;
	emailContent: string;
	emailImageUrl?: string;
	petitionTitle: string;
	userName: string;
};

export function PreviewModal({
	disabled,
	onPreview,
	emailTitle,
	emailContent,
	emailImageUrl,
	petitionTitle,
	userName,
}: PreviewModalProps): JSX.Element | null {
	const { petitionUpdateEmailPreviewEnabled } = useStarterDashboardV2PageFcm();
	const track = useTracking();

	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const onCloseModal = useCallback(() => {
		void track(PREVIEW_CLOSE_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			victory: false,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);
	const { translate } = useI18n();

	if (!petitionUpdateEmailPreviewEnabled) {
		return null;
	}

	return (
		<Modal
			onClose={onCloseModal}
			trigger={
				<PreviewEmailButton data-qa="petition-update-preview-email-button" disabled={disabled} onClick={onPreview} />
			}
		>
			<ModalHeader
				title={translate('corgi.petition.starter_dashboard_v2.post_an_update.preview.title')}
				subtitle={translate('corgi.petition.starter_dashboard_v2.post_an_update.preview.subtitle')}
			/>

			<ModalBody>
				<PreviewEmailModalBody
					emailTitle={emailTitle}
					emailContent={emailContent}
					imageDataUrl={emailImageUrl}
					petitionTitle={petitionTitle}
					userName={userName}
				/>
			</ModalBody>

			<ModalFooter>
				<Button data-qa="close-preview-email-modal-button" variant="primary" onClick={onCloseModal}>
					Close preview
				</Button>
			</ModalFooter>
		</Modal>
	);
}
